import { PlatformError } from '@djgg/core';
import {
  canonicalJson,
  assertConfigSchema,
  validateConfigSchema,
  validateCapabilitySelection,
  validateGameplayCapabilityLock,
  verifyGameplayCapabilityLock,
  GAMEPLAY_SEMANTICS,
  type CapabilitySelection,
  type GameplaySemantic,
  type GameplayCapabilityLockV1,
  type JsonObject,
  type ConfigSchema,
  validateJsonObject,
} from '@djgg/gameplay-kit';
import {
  BOT_PURPOSES,
  MATCH_PLAYER_LIMIT,
  validateMatchConfigV1,
  assertValidWorldEventV2,
  type BotPurpose,
  type MatchConfigV1,
  type WorldEventV2,
} from '@djgg/protocol';
import type { CreationAssetKind } from './types.js';
import { ANIM_STATE_NAMES, type AnimStateName } from './animation.js';
import { RUNTIME_CONTRACT_FEATURES, type RuntimeContractFeature } from './runtimeRef.js';

export type MatchModeV1 = 'solo' | 'vsBot' | 'localVersus' | 'onlineVersus';

export interface TeamShapeV1 {
  playerCount: number;
  teamBySlot: number[];
}

export interface MatchConfigSchemaV1 {
  schemaVersion: 1;
  allowedModes: MatchModeV1[];
  playerCounts: number[];
  teamShapes: TeamShapeV1[];
  chapters: { options: string[]; default?: string };
  loadouts: Array<{ key: string; options: string[]; default: string }>;
  /** Every selectable rule has a bounded schema and a valid resolved default.
   * Empty legacy declarations may omit schemas. */
  rules: { allowedKeys: string[]; defaults: JsonObject; schemas?: Record<string, ConfigSchema> };
  rankedRuleset: { id: string; rules: JsonObject } | null;
  botFill: { allowed: boolean; maxBots: number; purposes: BotPurpose[] };
  spectators: { allowed: boolean; maxSpectators: number };
}

export interface MatchRulesDesign {
  schema: MatchConfigSchemaV1;
}

export interface ActionDesign {
  id: string;
  label: string;
  kind: 'continuous' | 'discrete' | 'edge-triggered' | 'held' | 'targeted';
  /** Declarative shell animation for an accepted action; never an authority
   * for action legality or simulation timing. */
  animation?: AnimStateName;
}

export interface ServiceWorkflowDesign {
  id: string;
  summary: string;
}

export interface GameplayDesignV1 {
  contractVersion: 1;
  capabilities: CapabilitySelection[];
  match: MatchRulesDesign;
  actions?: ActionDesign[];
  service?: ServiceWorkflowDesign;
}

export type MatchHudPresetV1 =
  | 'duel' | 'teams' | 'sports' | 'race' | 'score-attack' | 'survival'
  | 'service' | 'minimal-arcade';
export type HudResponsiveTargetV1 = 'compact' | 'regular' | 'wide';
export interface HudResponsiveVariantV1 {
  target: HudResponsiveTargetV1;
  layout: 'stacked' | 'split' | 'top-rail';
  minWidth: number;
}
export interface MatchHudSpecV1 {
  schemaVersion: 1;
  preset: MatchHudPresetV1;
  score?: { label: string; format: 'integer' | 'fraction' | 'ordinal'; secondaryMetrics: string[] };
  clock?: { display: 'hidden' | 'countdown' | 'countup'; warningTicks: number[] };
  labels: { score?: string; clock?: string; objective?: string; period?: string; round?: string };
  /** Keys are stable slot/team ids and values are static presentation colors. */
  colors: { slots: Record<string, string>; teams: Record<string, string> };
  visual: {
    typeScale: 'compact' | 'regular' | 'large';
    numberTreatment: 'plain' | 'outlined' | 'tabular';
    frameAsset?: string;
    announcement: 'none' | 'subtle' | 'prominent';
  };
  responsive: HudResponsiveVariantV1[];
  safeArea: 'avoid';
  controls: 'avoid';
}
export interface MatchPresentationSpecV1 { schemaVersion: 1; hud: MatchHudSpecV1; }

export type SceneAnchorV1 = 'bottom' | 'center' | 'top-left' | { x: number; y: number };
export type SceneLayerRoleV1 = 'actor' | 'background-prop' | 'foreground-prop' | 'projectile' | 'terrain';
export interface NormalizedSceneBoundsV1 { x: number; y: number; w: number; h: number; }
export interface SceneRoleSpecV1 { entityKind: string; assetName?: string; expectedAnchor: SceneAnchorV1; visualBounds?: NormalizedSceneBoundsV1; collisionRef?: string; layerRole?: SceneLayerRoleV1; }
export interface SceneCompositionSpecV1 { schemaVersion: 1; roles: Record<string, SceneRoleSpecV1>; debugOverlay: 'available'; }

/** Validated against the shared semantic-event vocabulary by the sidecar
 * writer. The shell receives this as an opaque, already-authoritative id. */
export type RuntimeSemanticEventIdV1 = GameplaySemantic;
export type RuntimeVfxIntentV1 =
  | { effect: 'none' }
  | { effect: 'burst'; count: number; palette?: number | 'slot' }
  | { effect: 'flash'; palette?: number | 'slot' }
  | { effect: 'popup'; text: string; palette?: number | 'slot' }
  | { effect: 'shake' };
/** Mirrors the shell's closed transform-reaction vocabulary. The local copy is
 * type-pinned by the structural consumer parity test because creation bundles
 * cannot import creation-shell internals. */
export type RuntimeReactionV1 = 'blink' | 'flicker' | 'pop' | 'spin' | 'squash';
export type RuntimeEffectTargetV1 = 'actor' | 'target' | 'position' | 'global';
/** Exact, conjunctive matching only. Data predicates address bounded top-level
 * scalar keys, never executable expressions, nested paths or regexes. */
export interface RuntimeEventSelectorV1 {
  kind?: string;
  dataEquals?: Record<string, string | number | boolean | null>;
}
export type RuntimeEffectIntentV1 =
  | { kind: 'animation'; state: AnimStateName; target?: 'actor' | 'target' }
  | { kind: 'reaction'; reaction: RuntimeReactionV1; target?: 'actor' | 'target' }
  | { kind: 'vfx'; vfx: RuntimeVfxIntentV1; target?: RuntimeEffectTargetV1 }
  | { kind: 'sfx'; mediaId: string; target?: RuntimeEffectTargetV1 }
  | { kind: 'hud-flash'; color: string; target?: 'global' }
  | { kind: 'hit-stop'; durationMs: number }
  | { kind: 'music-duck'; durationMs: number; factor: number }
  | { kind: 'none' };
/** First matching binding wins, in array order; intents execute in array order.
 * Omitted selector matches the whole semantic and is its last fallback.
 * Legacy targets default to actor for animation/reaction, position for VFX,
 * and global for SFX/HUD. Missing event references skip an effect, never retarget it. */
export interface RuntimeEffectBindingV1 { event: RuntimeSemanticEventIdV1; selector?: RuntimeEventSelectorV1; intents: RuntimeEffectIntentV1[]; }
export interface EffectsBindingV1 { schemaVersion: 1; bindings: RuntimeEffectBindingV1[]; }

/** This is deliberately additive. Existing CreationAssetKind endpoints and
 * writers remain visual-only until the media phase enables them. */
export type CreationMediaKindV1 = CreationAssetKind | 'music' | 'sfx';
export type RuntimeMediaBindingV1 =
  | { kind: 'music'; id: string; contentSha256: string; loop: 'match' }
  | { kind: 'sfx'; id: string; contentSha256: string; event: RuntimeSemanticEventIdV1 };

export interface ProjectionDeclarationV1 {
  schemaVersion: 1;
  mode: 'identity' | 'per-viewer';
  spectatorPolicy: 'allow' | 'deny';
}

export interface DemoPlanV1 {
  schemaVersion: 1;
  version: number;
  artifactDigest: string;
  capabilityDigest: string;
  matchConfig: MatchConfigV1;
  seed: number;
  commands: Array<{ tick: number; slot: number; command: JsonObject }>;
  durationTicks: number;
  loopPolicy: 'reseed' | 'restart-tape';
}

export interface CreationRuntimeContractV1 {
  schemaVersion: 1;
  creationId: string;
  version: number;
  protocolVersion: 1 | 2;
  shellApi: number;
  shellVersion: string;
  simSha256: string;
  sourceSha256: string;
  capabilityLock: GameplayCapabilityLockV1;
  matchConfigSchema: MatchConfigSchemaV1;
  presentation: MatchPresentationSpecV1;
  scene: SceneCompositionSpecV1;
  effects: EffectsBindingV1;
  media: RuntimeMediaBindingV1[];
  projection: ProjectionDeclarationV1;
  demo?: DemoPlanV1;
}

/**
 * Derive trusted-row feature metadata from the complete validated sidecar.
 * Neither builders nor readers maintain a second hand-written feature list.
 */
export function runtimeContractFeatures(contract: CreationRuntimeContractV1): RuntimeContractFeature[] {
  const selected: RuntimeContractFeature[] = ['match-hud'];
  if (Object.keys(contract.scene.roles).length > 0) selected.push('scene-composition');
  if (contract.effects.bindings.length > 0) selected.push('effects-binding');
  if (contract.media.length > 0) selected.push('version-media');
  if (contract.projection.mode === 'per-viewer') selected.push('per-viewer-projection');
  if (contract.demo !== undefined) selected.push('demo-plan');
  return selected.sort().filter((feature, index, all) => index === 0 || feature !== all[index - 1])
    .filter((feature) => RUNTIME_CONTRACT_FEATURES.includes(feature));
}

const SHA256 = /^[0-9a-f]{64}$/;
const HEX = /^#[0-9a-f]{6}$/;
const ID = /^[a-z][a-z0-9-]{0,63}$/;
const CREATION_ID = /^[A-Za-z0-9_-]{1,128}$/;

function fail(path: string, detail: string): never {
  throw new PlatformError('validation', `invalid runtime contract at ${path}: ${detail}`);
}
function object(value: unknown, path: string): Record<string, unknown> {
  try { return validateJsonObject(value, path); }
  catch (error) { fail(path, error instanceof Error ? error.message : 'must be a strict JSON object'); }
}
function only(value: Record<string, unknown>, path: string, keys: readonly string[]): void {
  for (const key of Object.keys(value)) if (!keys.includes(key)) fail(path, `has unknown key ${JSON.stringify(key)}`);
}
function text(value: unknown, path: string, max = 160): string {
  if (typeof value !== 'string' || !value.trim() || value.length > max) fail(path, `must be non-empty text up to ${max} chars`);
  return value;
}
function id(value: unknown, path: string): string {
  if (typeof value !== 'string' || !ID.test(value)) fail(path, 'must be a lowercase identifier');
  return value;
}
function creationId(value: unknown, path: string): string {
  if (typeof value !== 'string' || !CREATION_ID.test(value)) fail(path, 'must be a path-safe creation id');
  return value;
}
function digest(value: unknown, path: string): string {
  if (typeof value !== 'string' || !SHA256.test(value)) fail(path, 'must be a 64-character lowercase SHA-256 digest');
  return value;
}
function int(value: unknown, path: string, min: number, max: number): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) fail(path, `must be an integer from ${min} to ${max}`);
  return value;
}
function orderedUnique(values: unknown, path: string, reader: (value: unknown, path: string) => string): string[] {
  if (!Array.isArray(values)) fail(path, 'must be an array');
  const output = values.map((value, index) => reader(value, `${path}[${index}]`));
  if (new Set(output).size !== output.length) fail(path, 'must not contain duplicates');
  const sorted = [...output].sort();
  if (output.some((value, index) => value !== sorted[index])) fail(path, 'must be sorted by code-unit order');
  return output;
}

export function validateMatchConfigSchemaV1(value: unknown, path = 'matchConfigSchema'): MatchConfigSchemaV1 {
  const v = object(value, path);
  only(v, path, ['schemaVersion', 'allowedModes', 'playerCounts', 'teamShapes', 'chapters', 'loadouts', 'rules', 'rankedRuleset', 'botFill', 'spectators']);
  if (v.schemaVersion !== 1) fail(`${path}.schemaVersion`, 'must equal 1');
  if (!Array.isArray(v.allowedModes) || v.allowedModes.length === 0) fail(`${path}.allowedModes`, 'must be a non-empty array');
  const allowedModes = orderedUnique(v.allowedModes, `${path}.allowedModes`, (item, itemPath) => {
    if (item !== 'solo' && item !== 'vsBot' && item !== 'localVersus' && item !== 'onlineVersus') fail(itemPath, 'is not a supported match mode');
    return item;
  }) as MatchModeV1[];
  if (!Array.isArray(v.playerCounts) || v.playerCounts.length === 0) fail(`${path}.playerCounts`, 'must be a non-empty array');
  const playerCounts = v.playerCounts.map((item, index) => int(item, `${path}.playerCounts[${index}]`, 1, MATCH_PLAYER_LIMIT));
  if (new Set(playerCounts).size !== playerCounts.length || playerCounts.some((count, index) => index > 0 && count <= playerCounts[index - 1])) fail(`${path}.playerCounts`, 'must be unique ascending integers');
  if (!Array.isArray(v.teamShapes) || v.teamShapes.length === 0 || v.teamShapes.length > 128) fail(`${path}.teamShapes`, 'must contain 1..128 shapes');
  const teamShapes = v.teamShapes.map((item, index) => {
    const shape = object(item, `${path}.teamShapes[${index}]`);
    only(shape, `${path}.teamShapes[${index}]`, ['playerCount', 'teamBySlot']);
    const playerCount = int(shape.playerCount, `${path}.teamShapes[${index}].playerCount`, 1, MATCH_PLAYER_LIMIT);
    if (!playerCounts.includes(playerCount)) fail(`${path}.teamShapes[${index}].playerCount`, 'must be declared in playerCounts');
    if (!Array.isArray(shape.teamBySlot) || shape.teamBySlot.length !== playerCount) fail(`${path}.teamShapes[${index}].teamBySlot`, 'must align with playerCount');
    const teamBySlot = shape.teamBySlot.map((slot, slotIndex) => int(slot, `${path}.teamShapes[${index}].teamBySlot[${slotIndex}]`, 0, playerCount - 1));
    const seen = new Set<number>();
    for (const team of teamBySlot) {
      if (!seen.has(team) && team !== seen.size) fail(`${path}.teamShapes[${index}].teamBySlot`, 'team ids must be contiguous in first-seat order');
      seen.add(team);
    }
    return { playerCount, teamBySlot };
  });
  const teamShapeKeys = teamShapes.map((shape) => `${shape.playerCount}:${shape.teamBySlot.join(',')}`);
  if (new Set(teamShapeKeys).size !== teamShapeKeys.length) fail(`${path}.teamShapes`, 'must not duplicate a team shape');
  for (const playerCount of playerCounts) if (!teamShapes.some((shape) => shape.playerCount === playerCount)) fail(`${path}.teamShapes`, `must declare a shape for playerCount ${playerCount}`);
  const chaptersValue = object(v.chapters, `${path}.chapters`);
  only(chaptersValue, `${path}.chapters`, ['options', 'default']);
  const chapters = { options: orderedUnique(chaptersValue.options, `${path}.chapters.options`, id), ...(chaptersValue.default === undefined ? {} : { default: id(chaptersValue.default, `${path}.chapters.default`) }) };
  if (chapters.options.length > 128) fail(`${path}.chapters.options`, 'must contain at most 128 chapters');
  if (chapters.default !== undefined && !chapters.options.includes(chapters.default)) fail(`${path}.chapters.default`, 'must be one of options');
  if (!Array.isArray(v.loadouts) || v.loadouts.length > 32) fail(`${path}.loadouts`, 'must contain at most 32 loadouts');
  const loadoutKeys = new Set<string>();
  const loadouts = v.loadouts.map((item, index) => {
    const loadout = object(item, `${path}.loadouts[${index}]`);
    only(loadout, `${path}.loadouts[${index}]`, ['key', 'options', 'default']);
    const key = id(loadout.key, `${path}.loadouts[${index}].key`);
    if (loadoutKeys.has(key)) fail(`${path}.loadouts[${index}].key`, 'is duplicated');
    loadoutKeys.add(key);
    const options = orderedUnique(loadout.options, `${path}.loadouts[${index}].options`, id);
    if (options.length > 128) fail(`${path}.loadouts[${index}].options`, 'must contain at most 128 options');
    const fallback = id(loadout.default, `${path}.loadouts[${index}].default`);
    if (!options.includes(fallback)) fail(`${path}.loadouts[${index}].default`, 'must be one of options');
    return { key, options, default: fallback };
  });
  const rulesValue = object(v.rules, `${path}.rules`);
  only(rulesValue, `${path}.rules`, ['allowedKeys', 'defaults', 'schemas']);
  const allowedKeys = orderedUnique(rulesValue.allowedKeys, `${path}.rules.allowedKeys`, id);
  if (allowedKeys.length > 32) fail(`${path}.rules.allowedKeys`, 'must contain at most 32 rules');
  const defaults = object(rulesValue.defaults, `${path}.rules.defaults`) as JsonObject;
  const schemas: Record<string, ConfigSchema> = {};
  const schemaValues = rulesValue.schemas === undefined ? {} : object(rulesValue.schemas, `${path}.rules.schemas`);
  for (const key of Object.keys(schemaValues)) {
    if (!allowedKeys.includes(key)) fail(`${path}.rules.schemas.${key}`, 'is not declared by allowedKeys');
    schemas[key] = validateConfigSchema(schemaValues[key], `${path}.rules.schemas.${key}`);
  }
  for (const key of allowedKeys) if (!Object.hasOwn(schemas, key)) fail(`${path}.rules.schemas.${key}`, 'every allowed rule requires a bounded schema');
  const rules: MatchConfigSchemaV1['rules'] = { allowedKeys, defaults, ...(rulesValue.schemas === undefined ? {} : { schemas }) };
  validateRuleSelections(defaults, rules, `${path}.rules.defaults`);
  const rankedRuleset = v.rankedRuleset === null ? null : (() => {
    const ranked = object(v.rankedRuleset, `${path}.rankedRuleset`);
    only(ranked, `${path}.rankedRuleset`, ['id', 'rules']);
    const selected = object(ranked.rules, `${path}.rankedRuleset.rules`) as JsonObject;
    validateRuleSelections(selected, rules, `${path}.rankedRuleset.rules`);
    return { id: id(ranked.id, `${path}.rankedRuleset.id`), rules: selected };
  })();
  const botFillValue = object(v.botFill, `${path}.botFill`);
  only(botFillValue, `${path}.botFill`, ['allowed', 'maxBots', 'purposes']);
  if (typeof botFillValue.allowed !== 'boolean') fail(`${path}.botFill.allowed`, 'must be boolean');
  if (!Array.isArray(botFillValue.purposes)) fail(`${path}.botFill.purposes`, 'must be an array');
  const purposes = botFillValue.purposes.map((purpose, index) => { if (typeof purpose !== 'string' || !BOT_PURPOSES.includes(purpose as BotPurpose)) fail(`${path}.botFill.purposes[${index}]`, 'is not a bot purpose'); return purpose as BotPurpose; });
  if (new Set(purposes).size !== purposes.length) fail(`${path}.botFill.purposes`, 'must not contain duplicates');
  const botFill = { allowed: botFillValue.allowed, maxBots: int(botFillValue.maxBots, `${path}.botFill.maxBots`, 0, MATCH_PLAYER_LIMIT), purposes };
  if (!botFill.allowed && (botFill.maxBots !== 0 || purposes.length !== 0)) fail(`${path}.botFill`, 'disabled bot fill must have zero bots and no purposes');
  if (botFill.allowed && (botFill.maxBots === 0 || purposes.length === 0 || botFill.maxBots > Math.max(...playerCounts))) fail(`${path}.botFill`, 'enabled bot fill requires usable seats and purposes');
  const spectatorsValue = object(v.spectators, `${path}.spectators`);
  only(spectatorsValue, `${path}.spectators`, ['allowed', 'maxSpectators']);
  if (typeof spectatorsValue.allowed !== 'boolean') fail(`${path}.spectators.allowed`, 'must be boolean');
  const spectators = { allowed: spectatorsValue.allowed, maxSpectators: int(spectatorsValue.maxSpectators, `${path}.spectators.maxSpectators`, 0, 100) };
  if (!spectators.allowed && spectators.maxSpectators !== 0) fail(`${path}.spectators.maxSpectators`, 'must be 0 when spectators are denied');
  if (spectators.allowed && spectators.maxSpectators === 0) fail(`${path}.spectators.maxSpectators`, 'must be positive when spectators are allowed');
  const modeSupportsCount = (mode: MatchModeV1, count: number) => mode === 'solo' ? count === 1
    : count >= 2 && (mode !== 'vsBot' || botFill.allowed && botFill.maxBots >= 1);
  for (const mode of allowedModes) if (!playerCounts.some(count => modeSupportsCount(mode, count))) fail(`${path}.allowedModes`, `${mode} has no satisfiable player/bot topology`);
  for (const count of playerCounts) if (!allowedModes.some(mode => modeSupportsCount(mode, count))) fail(`${path}.playerCounts`, `${count} has no satisfiable match mode`);
  for (const purpose of botFill.purposes) {
    const usable = allowedModes.some(mode => playerCounts.some(count => modeSupportsCount(mode, count)
      && (mode === 'vsBot' || (mode === 'onlineVersus' || mode === 'localVersus') && count >= 3
        || (purpose === 'demo' || purpose === 'evaluation') && botFill.maxBots >= count)));
    if (!usable) fail(`${path}.botFill.purposes`, `${purpose} has no satisfiable bot-controlled topology`);
  }
  if (rankedRuleset !== null && (!allowedModes.includes('onlineVersus')
    || !teamShapes.some(shape => shape.playerCount === 2 && shape.teamBySlot[0] === 0 && shape.teamBySlot[1] === 1))) fail(`${path}.rankedRuleset`, 'requires a satisfiable two-human opposing-team online topology');
  return { schemaVersion: 1, allowedModes, playerCounts, teamShapes, chapters, loadouts, rules, rankedRuleset, botFill, spectators };
}

function validateRuleSelections(value: JsonObject, rules: MatchConfigSchemaV1['rules'], path: string): void {
  if (new TextEncoder().encode(canonicalJson(value)).length > 32 * 1024) fail(path, 'exceeds 32 KiB canonical JSON');
  for (const key of Object.keys(value)) if (!rules.allowedKeys.includes(key)) fail(`${path}.${key}`, 'is not declared by allowedKeys');
  for (const key of rules.allowedKeys) {
    if (!Object.hasOwn(value, key)) fail(`${path}.${key}`, 'must explicitly resolve every declared rule');
    const schema = rules.schemas?.[key];
    if (schema === undefined) fail(`${path}.${key}`, 'requires a bounded rule schema');
    assertConfigSchema(value[key]!, schema, `${path}.${key}`);
  }
}

/** Validates a frozen protocol MatchConfig against creation-authored choices. */
export function validateMatchConfigAgainstSchema(value: unknown, schemaValue: unknown, path = 'matchConfig'): MatchConfigV1 {
  const config = validateMatchConfigV1(value, path);
  const schema = validateMatchConfigSchemaV1(schemaValue, 'matchConfigSchema');
  if (!schema.allowedModes.includes(config.mode)) fail(`${path}.mode`, 'is not allowed by this creation');
  if (!schema.playerCounts.includes(config.playerCount)) fail(`${path}.playerCount`, 'is not allowed by this creation');
  const allowedShape = schema.teamShapes.some((candidate) => candidate.playerCount === config.playerCount
    && candidate.teamBySlot.length === config.teamBySlot.length
    && candidate.teamBySlot.every((team, index) => team === config.teamBySlot[index]));
  if (!allowedShape) fail(`${path}.teamBySlot`, 'does not match a declared team shape');
  if (schema.chapters.options.length > 0 && config.chapter === undefined) fail(`${path}.chapter`, 'must explicitly resolve the selected chapter');
  if (config.chapter !== undefined && !schema.chapters.options.includes(config.chapter)) fail(`${path}.chapter`, 'is not an allowed chapter');
  for (const [slot, loadout] of config.loadoutBySlot.entries()) {
    for (const choice of schema.loadouts) {
      if (loadout[choice.key] === undefined) fail(`${path}.loadoutBySlot[${slot}].${choice.key}`, 'must explicitly resolve every declared loadout');
      if (!choice.options.includes(loadout[choice.key]!)) fail(`${path}.loadoutBySlot[${slot}].${choice.key}`, 'is not an allowed option');
    }
    for (const [key, option] of Object.entries(loadout)) {
      const choice = schema.loadouts.find((candidate) => candidate.key === key);
      if (!choice || !choice.options.includes(option)) fail(`${path}.loadoutBySlot[${slot}]`, `contains disallowed ${key}`);
    }
  }
  validateRuleSelections(config.rules, schema.rules, `${path}.rules`);
  if (config.ranked && schema.rankedRuleset === null) fail(`${path}.ranked`, 'requires a declared ranked ruleset');
  if (config.ranked && schema.rankedRuleset !== null && canonicalJson(config.rules) !== canonicalJson(schema.rankedRuleset.rules)) fail(`${path}.rules`, 'must exactly match the ranked ruleset');
  if (!schema.botFill.allowed && config.bots.length > 0) fail(`${path}.bots`, 'are disabled by this creation');
  if (config.bots.length > schema.botFill.maxBots) fail(`${path}.bots`, 'exceeds the declared bot limit');
  for (const [index, bot] of config.bots.entries()) if (!schema.botFill.purposes.includes(bot.purpose)) fail(`${path}.bots[${index}].purpose`, 'is not allowed by this creation');
  return config;
}

export function validateGameplayDesignV1(value: unknown, path = 'gameplay'): GameplayDesignV1 {
  const v = object(value, path);
  only(v, path, ['contractVersion', 'capabilities', 'match', 'actions', 'service']);
  if (v.contractVersion !== 1) fail(`${path}.contractVersion`, 'must equal 1');
  if (!Array.isArray(v.capabilities)) fail(`${path}.capabilities`, 'must be an array');
  const capabilities = v.capabilities.map((selection, index) => {
    try {
      return validateCapabilitySelection(selection);
    } catch (error) {
      fail(`${path}.capabilities[${index}]`, error instanceof Error ? error.message : 'is invalid');
    }
  });
  const match = object(v.match, `${path}.match`);
  only(match, `${path}.match`, ['schema']);
  const out: GameplayDesignV1 = { contractVersion: 1, capabilities, match: { schema: validateMatchConfigSchemaV1(match.schema, `${path}.match.schema`) } };
  if (v.actions !== undefined) {
    if (!Array.isArray(v.actions)) fail(`${path}.actions`, 'must be an array');
    const ids = new Set<string>();
    out.actions = v.actions.map((action, index) => {
      const item = object(action, `${path}.actions[${index}]`); only(item, `${path}.actions[${index}]`, ['id', 'label', 'kind', 'animation']);
      const actionId = id(item.id, `${path}.actions[${index}].id`); if (ids.has(actionId)) fail(`${path}.actions[${index}].id`, 'is duplicated'); ids.add(actionId);
      if (item.kind !== 'continuous' && item.kind !== 'discrete' && item.kind !== 'edge-triggered' && item.kind !== 'held' && item.kind !== 'targeted') fail(`${path}.actions[${index}].kind`, 'is not supported');
      if (item.animation !== undefined && (typeof item.animation !== 'string' || !ANIM_STATE_NAMES.includes(item.animation as AnimStateName))) fail(`${path}.actions[${index}].animation`, 'is not an animation state');
      return { id: actionId, label: text(item.label, `${path}.actions[${index}].label`, 80), kind: item.kind, ...(item.animation === undefined ? {} : { animation: item.animation as AnimStateName }) };
    });
  }
  if (v.service !== undefined) { const service = object(v.service, `${path}.service`); only(service, `${path}.service`, ['id', 'summary']); out.service = { id: id(service.id, `${path}.service.id`), summary: text(service.summary, `${path}.service.summary`, 300) }; }
  return out;
}

export function validateMatchPresentationSpecV1(value: unknown, path = 'presentation'): MatchPresentationSpecV1 {
  const v = object(value, path); only(v, path, ['schemaVersion', 'hud']);
  if (v.schemaVersion !== 1) fail(`${path}.schemaVersion`, 'must equal 1');
  const hud = object(v.hud, `${path}.hud`);
  only(hud, `${path}.hud`, ['schemaVersion', 'preset', 'score', 'clock', 'labels', 'colors', 'visual', 'responsive', 'safeArea', 'controls']);
  if (hud.schemaVersion !== 1) fail(`${path}.hud.schemaVersion`, 'must equal 1');
  const presets: readonly MatchHudPresetV1[] = ['duel', 'teams', 'sports', 'race', 'score-attack', 'survival', 'service', 'minimal-arcade'];
  if (typeof hud.preset !== 'string' || !presets.includes(hud.preset as MatchHudPresetV1)) fail(`${path}.hud.preset`, 'is not supported');
  const labelsValue = object(hud.labels, `${path}.hud.labels`); only(labelsValue, `${path}.hud.labels`, ['score', 'clock', 'objective', 'period', 'round']);
  const labels: MatchHudSpecV1['labels'] = {};
  for (const [key, raw] of Object.entries(labelsValue)) labels[key as keyof typeof labels] = text(raw, `${path}.hud.labels.${key}`, 40);
  const colorsValue = object(hud.colors, `${path}.hud.colors`); only(colorsValue, `${path}.hud.colors`, ['slots', 'teams']);
  const colorMap = (raw: unknown, colorPath: string): Record<string, string> => {
    const source = object(raw, colorPath); const result: Record<string, string> = {};
    for (const [key, color] of Object.entries(source)) { id(key, `${colorPath} key`); if (typeof color !== 'string' || !HEX.test(color)) fail(`${colorPath}.${key}`, 'must be #rrggbb'); result[key] = color; }
    return result;
  };
  const colors = { slots: colorMap(colorsValue.slots, `${path}.hud.colors.slots`), teams: colorMap(colorsValue.teams, `${path}.hud.colors.teams`) };
  const visualValue = object(hud.visual, `${path}.hud.visual`); only(visualValue, `${path}.hud.visual`, ['typeScale', 'numberTreatment', 'frameAsset', 'announcement']);
  if (visualValue.typeScale !== 'compact' && visualValue.typeScale !== 'regular' && visualValue.typeScale !== 'large') fail(`${path}.hud.visual.typeScale`, 'is not supported');
  if (visualValue.numberTreatment !== 'plain' && visualValue.numberTreatment !== 'outlined' && visualValue.numberTreatment !== 'tabular') fail(`${path}.hud.visual.numberTreatment`, 'is not supported');
  if (visualValue.announcement !== 'none' && visualValue.announcement !== 'subtle' && visualValue.announcement !== 'prominent') fail(`${path}.hud.visual.announcement`, 'is not supported');
  const visual = { typeScale: visualValue.typeScale, numberTreatment: visualValue.numberTreatment, ...(visualValue.frameAsset === undefined ? {} : { frameAsset: id(visualValue.frameAsset, `${path}.hud.visual.frameAsset`) }), announcement: visualValue.announcement } as MatchHudSpecV1['visual'];
  let score: MatchHudSpecV1['score'];
  if (hud.score !== undefined) { const item = object(hud.score, `${path}.hud.score`); only(item, `${path}.hud.score`, ['label', 'format', 'secondaryMetrics']); if (item.format !== 'integer' && item.format !== 'fraction' && item.format !== 'ordinal') fail(`${path}.hud.score.format`, 'is not supported'); score = { label: text(item.label, `${path}.hud.score.label`, 40), format: item.format, secondaryMetrics: orderedUnique(item.secondaryMetrics, `${path}.hud.score.secondaryMetrics`, id) as string[] }; }
  let clock: MatchHudSpecV1['clock'];
  if (hud.clock !== undefined) { const item = object(hud.clock, `${path}.hud.clock`); only(item, `${path}.hud.clock`, ['display', 'warningTicks']); if (item.display !== 'hidden' && item.display !== 'countdown' && item.display !== 'countup') fail(`${path}.hud.clock.display`, 'is not supported'); if (!Array.isArray(item.warningTicks)) fail(`${path}.hud.clock.warningTicks`, 'must be an array'); const warningTicks = item.warningTicks.map((tick, index) => int(tick, `${path}.hud.clock.warningTicks[${index}]`, 0, 2 ** 31 - 1)); if (warningTicks.some((tick, index) => index > 0 && tick <= warningTicks[index - 1])) fail(`${path}.hud.clock.warningTicks`, 'must be unique ascending integers'); clock = { display: item.display, warningTicks }; }
  if (!Array.isArray(hud.responsive) || hud.responsive.length === 0) fail(`${path}.hud.responsive`, 'must be non-empty');
  const responsive = hud.responsive.map((variant, index) => { const item = object(variant, `${path}.hud.responsive[${index}]`); only(item, `${path}.hud.responsive[${index}]`, ['target', 'layout', 'minWidth']); if (item.target !== 'compact' && item.target !== 'regular' && item.target !== 'wide') fail(`${path}.hud.responsive[${index}].target`, 'is not supported'); if (item.layout !== 'stacked' && item.layout !== 'split' && item.layout !== 'top-rail') fail(`${path}.hud.responsive[${index}].layout`, 'is not supported'); return { target: item.target, layout: item.layout, minWidth: int(item.minWidth, `${path}.hud.responsive[${index}].minWidth`, 0, 10000) } as HudResponsiveVariantV1; });
  if (new Set(responsive.map((variant) => variant.target)).size !== responsive.length) fail(`${path}.hud.responsive`, 'must not repeat a target');
  if (hud.safeArea !== 'avoid' || hud.controls !== 'avoid') fail(`${path}.hud`, 'must avoid safe areas and occupied controls');
  return { schemaVersion: 1, hud: { schemaVersion: 1, preset: hud.preset as MatchHudPresetV1, ...(score === undefined ? {} : { score }), ...(clock === undefined ? {} : { clock }), labels, colors, visual, responsive, safeArea: 'avoid', controls: 'avoid' } };
}

function validateSceneAnchor(value: unknown, path: string): SceneAnchorV1 {
  if (value === 'center' || value === 'bottom' || value === 'top-left') return value;
  const anchor = object(value, path); only(anchor, path, ['x', 'y']);
  return { x: number(anchor.x, `${path}.x`), y: number(anchor.y, `${path}.y`) };
}
function validateVisualBounds(value: unknown, path: string): NormalizedSceneBoundsV1 {
  const bounds = object(value, path); only(bounds, path, ['x', 'y', 'w', 'h']);
  const result = { x: number(bounds.x, `${path}.x`), y: number(bounds.y, `${path}.y`), w: number(bounds.w, `${path}.w`), h: number(bounds.h, `${path}.h`) };
  if (result.x + result.w > 1 || result.y + result.h > 1) fail(path, 'must stay within the normalized asset');
  return result;
}
export function validateSceneCompositionSpecV1(value: unknown, path = 'scene'): SceneCompositionSpecV1 {
  const v = object(value, path); only(v, path, ['schemaVersion', 'roles', 'debugOverlay']);
  if (v.schemaVersion !== 1) fail(`${path}.schemaVersion`, 'must equal 1');
  if (v.debugOverlay !== 'available') fail(`${path}.debugOverlay`, 'must equal available');
  const rolesValue = object(v.roles, `${path}.roles`); const roles: Record<string, SceneRoleSpecV1> = {};
  for (const [roleId, raw] of Object.entries(rolesValue)) {
    id(roleId, `${path}.roles key`);
    const role = object(raw, `${path}.roles.${roleId}`);
    only(role, `${path}.roles.${roleId}`, ['entityKind', 'assetName', 'expectedAnchor', 'visualBounds', 'collisionRef', 'layerRole']);
    if (role.layerRole !== undefined && role.layerRole !== 'actor' && role.layerRole !== 'background-prop' && role.layerRole !== 'foreground-prop' && role.layerRole !== 'projectile' && role.layerRole !== 'terrain') fail(`${path}.roles.${roleId}.layerRole`, 'is not supported');
    roles[roleId] = { entityKind: id(role.entityKind, `${path}.roles.${roleId}.entityKind`), ...(role.assetName === undefined ? {} : { assetName: id(role.assetName, `${path}.roles.${roleId}.assetName`) }), expectedAnchor: validateSceneAnchor(role.expectedAnchor, `${path}.roles.${roleId}.expectedAnchor`), ...(role.visualBounds === undefined ? {} : { visualBounds: validateVisualBounds(role.visualBounds, `${path}.roles.${roleId}.visualBounds`) }), ...(role.collisionRef === undefined ? {} : { collisionRef: id(role.collisionRef, `${path}.roles.${roleId}.collisionRef`) }), ...(role.layerRole === undefined ? {} : { layerRole: role.layerRole }) };
  }
  return { schemaVersion: 1, roles, debugOverlay: 'available' };
}
function number(value: unknown, path: string): number { if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 1) fail(path, 'must be a finite normalized number from 0 to 1'); return value; }

function validateVfxIntent(value: unknown, path: string): RuntimeVfxIntentV1 {
  const visual = object(value, path); const effect = visual.effect;
  if (effect === 'none' || effect === 'shake') { only(visual, path, ['effect']); return { effect }; }
  if (effect === 'burst' || effect === 'flash') { only(visual, path, effect === 'burst' ? ['effect', 'count', 'palette'] : ['effect', 'palette']); const rawPalette = visual.palette; const palette: number | 'slot' | undefined = rawPalette === undefined ? undefined : rawPalette === 'slot' ? 'slot' : int(rawPalette, `${path}.palette`, 0, 31); return effect === 'burst' ? { effect, count: int(visual.count, `${path}.count`, 1, 32), ...(palette === undefined ? {} : { palette }) } : { effect, ...(palette === undefined ? {} : { palette }) }; }
  if (effect === 'popup') { only(visual, path, ['effect', 'text', 'palette']); const rawPalette = visual.palette; const palette: number | 'slot' | undefined = rawPalette === undefined ? undefined : rawPalette === 'slot' ? 'slot' : int(rawPalette, `${path}.palette`, 0, 31); return { effect, text: text(visual.text, `${path}.text`, 48), ...(palette === undefined ? {} : { palette }) }; }
  fail(`${path}.effect`, 'is not supported');
}
export function validateEffectsBindingV1(value: unknown, path = 'effects'): EffectsBindingV1 {
  const v = object(value, path);
  only(v, path, ['schemaVersion', 'bindings']);
  if (v.schemaVersion !== 1) fail(`${path}.schemaVersion`, 'must equal 1');
  if (!Array.isArray(v.bindings) || v.bindings.length > 128) fail(`${path}.bindings`, 'must contain at most 128 ordered bindings');
  const selectors = new Set<string>();
  const fallbacks = new Set<GameplaySemantic>();
  const bindings = v.bindings.map((binding, index): RuntimeEffectBindingV1 => {
    const itemPath = `${path}.bindings[${index}]`;
    const item = object(binding, itemPath);
    only(item, itemPath, ['event', 'selector', 'intents']);
    if (typeof item.event !== 'string' || !GAMEPLAY_SEMANTICS.includes(item.event as GameplaySemantic)) fail(`${itemPath}.event`, 'is not a gameplay semantic');
    const event = item.event as GameplaySemantic;
    if (fallbacks.has(event)) fail(itemPath, 'is unreachable after the semantic fallback');
    const selector = item.selector === undefined ? undefined : validateEventSelector(item.selector, `${itemPath}.selector`);
    const selectorKey = canonicalJson({ event, selector: selector ?? null });
    if (selectors.has(selectorKey)) fail(`${itemPath}.selector`, 'is duplicated');
    selectors.add(selectorKey);
    if (selector === undefined) fallbacks.add(event);
    if (!Array.isArray(item.intents) || item.intents.length === 0 || item.intents.length > 16) fail(`${itemPath}.intents`, 'must contain 1..16 ordered intents');
    const intents = item.intents.map((intent, intentIndex) => validateEffectIntent(intent, `${itemPath}.intents[${intentIndex}]`));
    if (intents.some(intent => intent.kind === 'none') && intents.length !== 1) fail(`${itemPath}.intents`, 'explicit none must be the only intent');
    return { event, ...(selector === undefined ? {} : { selector }), intents };
  });
  return { schemaVersion: 1, bindings };
}

function validateEventSelector(value: unknown, path: string): RuntimeEventSelectorV1 {
  const selector = object(value, path);
  only(selector, path, ['kind', 'dataEquals']);
  if (selector.kind === undefined && selector.dataEquals === undefined) fail(path, 'must select kind or dataEquals');
  const out: RuntimeEventSelectorV1 = {};
  if (selector.kind !== undefined) {
    const kind = text(selector.kind, `${path}.kind`, 64);
    if (!/^[A-Za-z0-9._:-]+$/.test(kind)) fail(`${path}.kind`, 'must be an event-kind identifier');
    out.kind = kind;
  }
  if (selector.dataEquals !== undefined) {
    const fields = object(selector.dataEquals, `${path}.dataEquals`);
    if (Object.keys(fields).length < 1 || Object.keys(fields).length > 8) fail(`${path}.dataEquals`, 'must contain 1..8 scalar predicates');
    const dataEquals: NonNullable<RuntimeEventSelectorV1['dataEquals']> = {};
    for (const [key, entry] of Object.entries(fields)) {
      if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/.test(key)) fail(`${path}.dataEquals`, 'requires top-level data keys');
      if (entry !== null && typeof entry !== 'boolean' && typeof entry !== 'number' && typeof entry !== 'string') fail(`${path}.dataEquals.${key}`, 'requires scalar equality data');
      if (typeof entry === 'string' && entry.length > 128) fail(`${path}.dataEquals.${key}`, 'exceeds 128 characters');
      dataEquals[key] = entry;
    }
    out.dataEquals = dataEquals;
  }
  return out;
}

function validateEffectIntent(value: unknown, path: string): RuntimeEffectIntentV1 {
  const intent = object(value, path);
  const target = intent.target;
  if (target !== undefined && target !== 'actor' && target !== 'target' && target !== 'position' && target !== 'global') fail(`${path}.target`, 'is not a supported event target');
  if (intent.kind === 'none') {
    only(intent, path, ['kind']);
    return { kind: 'none' };
  }
  if (intent.kind === 'hit-stop') {
    only(intent, path, ['kind', 'durationMs']);
    return { kind: 'hit-stop', durationMs: int(intent.durationMs, `${path}.durationMs`, 0, 200) };
  }
  if (intent.kind === 'music-duck') {
    only(intent, path, ['kind', 'durationMs', 'factor']);
    if (typeof intent.factor !== 'number' || !Number.isFinite(intent.factor) || intent.factor < 0 || intent.factor > 1) fail(`${path}.factor`, 'must be between 0 and 1');
    return { kind: 'music-duck', durationMs: int(intent.durationMs, `${path}.durationMs`, 0, 2000), factor: intent.factor };
  }
  if (intent.kind === 'animation' || intent.kind === 'reaction') {
    if (target !== undefined && target !== 'actor' && target !== 'target') fail(`${path}.target`, 'animation/reaction requires actor or target');
    const entityTarget: { target?: 'actor' | 'target' } = target === undefined ? {} : { target };
    if (intent.kind === 'animation') {
      only(intent, path, ['kind', 'state', 'target']);
      if (typeof intent.state !== 'string' || !ANIM_STATE_NAMES.includes(intent.state as AnimStateName)) fail(`${path}.state`, 'is not an animation state');
      return { kind: 'animation', state: intent.state as AnimStateName, ...entityTarget };
    }
    only(intent, path, ['kind', 'reaction', 'target']);
    if (!['blink', 'flicker', 'pop', 'spin', 'squash'].includes(intent.reaction as string)) fail(`${path}.reaction`, 'is not a reaction');
    return { kind: 'reaction', reaction: intent.reaction as RuntimeReactionV1, ...entityTarget };
  }
  const eventTarget: { target?: RuntimeEffectTargetV1 } = target === undefined ? {} : { target };
  if (intent.kind === 'vfx') {
    only(intent, path, ['kind', 'vfx', 'target']);
    return { kind: 'vfx', vfx: validateVfxIntent(intent.vfx, `${path}.vfx`), ...eventTarget };
  }
  if (intent.kind === 'sfx') {
    only(intent, path, ['kind', 'mediaId', 'target']);
    return { kind: 'sfx', mediaId: id(intent.mediaId, `${path}.mediaId`), ...eventTarget };
  }
  if (intent.kind === 'hud-flash') {
    only(intent, path, ['kind', 'color', 'target']);
    if (target !== undefined && target !== 'global') fail(`${path}.target`, 'HUD flash requires global target');
    if (typeof intent.color !== 'string' || !HEX.test(intent.color)) fail(`${path}.color`, 'must be #rrggbb');
    return { kind: 'hud-flash', color: intent.color, ...(target === undefined ? {} : { target }) };
  }
  fail(`${path}.kind`, 'is not supported');
}

/** Pure selector reference implementation for contract tests and later shell
 * consumers. Undefined means uncovered; an explicit none binding means covered. */
export function selectRuntimeEffectBindingV1(value: EffectsBindingV1, event: WorldEventV2): RuntimeEffectBindingV1 | undefined {
  const effects = validateEffectsBindingV1(value);
  assertValidWorldEventV2(event);
  return effects.bindings.find(binding => {
    if (binding.event !== event.semantic || binding.selector?.kind !== undefined && binding.selector.kind !== event.kind) return false;
    const predicates = binding.selector?.dataEquals;
    if (predicates === undefined) return true;
    const data = event.data;
    if (data === null || typeof data !== 'object' || Array.isArray(data)) return false;
    return Object.entries(predicates).every(([key, expected]) => Object.hasOwn(data, key) && data[key] === expected);
  });
}

export function validateRuntimeMediaBindingsV1(value: unknown, path = 'media'): RuntimeMediaBindingV1[] {
  if (!Array.isArray(value)) fail(path, 'must be an array'); const ids = new Set<string>();
  return value.map((binding, index) => { const item = object(binding, `${path}[${index}]`); if (item.kind === 'music') { only(item, `${path}[${index}]`, ['kind', 'id', 'contentSha256', 'loop']); const mediaId = id(item.id, `${path}[${index}].id`); if (item.loop !== 'match') fail(`${path}[${index}].loop`, 'must be match'); if (ids.has(mediaId)) fail(`${path}[${index}].id`, 'is duplicated'); ids.add(mediaId); return { kind: 'music', id: mediaId, contentSha256: digest(item.contentSha256, `${path}[${index}].contentSha256`), loop: 'match' }; } if (item.kind === 'sfx') { only(item, `${path}[${index}]`, ['kind', 'id', 'contentSha256', 'event']); const mediaId = id(item.id, `${path}[${index}].id`); if (ids.has(mediaId)) fail(`${path}[${index}].id`, 'is duplicated'); ids.add(mediaId); if (typeof item.event !== 'string' || !GAMEPLAY_SEMANTICS.includes(item.event as GameplaySemantic)) fail(`${path}[${index}].event`, 'is not a gameplay semantic'); return { kind: 'sfx', id: mediaId, contentSha256: digest(item.contentSha256, `${path}[${index}].contentSha256`), event: item.event as GameplaySemantic }; } fail(`${path}[${index}].kind`, 'must be music or sfx'); });
}

export function validateCreationRuntimeContractV1(value: unknown, path = 'runtimeContract'): CreationRuntimeContractV1 {
  const v = object(value, path); only(v, path, ['schemaVersion', 'creationId', 'version', 'protocolVersion', 'shellApi', 'shellVersion', 'simSha256', 'sourceSha256', 'capabilityLock', 'matchConfigSchema', 'presentation', 'scene', 'effects', 'media', 'projection', 'demo']);
  if (v.schemaVersion !== 1) fail(`${path}.schemaVersion`, 'must equal 1'); const trustedCreationId = creationId(v.creationId, `${path}.creationId`); const version = int(v.version, `${path}.version`, 1, 2 ** 31 - 1); if (v.protocolVersion !== 1 && v.protocolVersion !== 2) fail(`${path}.protocolVersion`, 'must be 1 or 2');
  let capabilityLock: GameplayCapabilityLockV1;
  try { capabilityLock = validateGameplayCapabilityLock(v.capabilityLock); }
  catch (error) { fail(`${path}.capabilityLock`, error instanceof Error ? error.message : 'is invalid'); }
  const matchConfigSchema = validateMatchConfigSchemaV1(v.matchConfigSchema, `${path}.matchConfigSchema`); const presentation = validateMatchPresentationSpecV1(v.presentation, `${path}.presentation`); const scene = validateSceneCompositionSpecV1(v.scene, `${path}.scene`); const effects = validateEffectsBindingV1(v.effects, `${path}.effects`); const media = validateRuntimeMediaBindingsV1(v.media, `${path}.media`);
  const sfxMedia = new Set(media.filter((entry): entry is Extract<RuntimeMediaBindingV1, { kind: 'sfx' }> => entry.kind === 'sfx').map((entry) => entry.id));
  for (const binding of effects.bindings) for (const intent of binding.intents) if (intent.kind === 'sfx' && !sfxMedia.has(intent.mediaId)) fail(`${path}.effects`, `references undeclared sfx media ${intent.mediaId}`);
  const projectionValue = object(v.projection, `${path}.projection`); only(projectionValue, `${path}.projection`, ['schemaVersion', 'mode', 'spectatorPolicy']); if (projectionValue.schemaVersion !== 1 || (projectionValue.mode !== 'identity' && projectionValue.mode !== 'per-viewer') || (projectionValue.spectatorPolicy !== 'allow' && projectionValue.spectatorPolicy !== 'deny')) fail(`${path}.projection`, 'has invalid declaration'); const projection: ProjectionDeclarationV1 = { schemaVersion: 1, mode: projectionValue.mode, spectatorPolicy: projectionValue.spectatorPolicy };
  if ((projection.spectatorPolicy === 'allow') !== matchConfigSchema.spectators.allowed) fail(`${path}.projection.spectatorPolicy`, 'must agree with matchConfigSchema.spectators');
  const contract: CreationRuntimeContractV1 = { schemaVersion: 1, creationId: trustedCreationId, version, protocolVersion: v.protocolVersion, shellApi: int(v.shellApi, `${path}.shellApi`, 1, 999), shellVersion: text(v.shellVersion, `${path}.shellVersion`, 80), simSha256: digest(v.simSha256, `${path}.simSha256`), sourceSha256: digest(v.sourceSha256, `${path}.sourceSha256`), capabilityLock, matchConfigSchema, presentation, scene, effects, media, projection };
  if (v.demo !== undefined) { const demoValue = object(v.demo, `${path}.demo`); only(demoValue, `${path}.demo`, ['schemaVersion', 'version', 'artifactDigest', 'capabilityDigest', 'matchConfig', 'seed', 'commands', 'durationTicks', 'loopPolicy']); if (demoValue.schemaVersion !== 1 || demoValue.loopPolicy !== 'reseed' && demoValue.loopPolicy !== 'restart-tape') fail(`${path}.demo`, 'has invalid version or loop policy'); const matchConfig = validateMatchConfigAgainstSchema(demoValue.matchConfig, matchConfigSchema, `${path}.demo.matchConfig`); const demo: DemoPlanV1 = { schemaVersion: 1, version: int(demoValue.version, `${path}.demo.version`, 1, 2 ** 31 - 1), artifactDigest: digest(demoValue.artifactDigest, `${path}.demo.artifactDigest`), capabilityDigest: digest(demoValue.capabilityDigest, `${path}.demo.capabilityDigest`), matchConfig, seed: int(demoValue.seed, `${path}.demo.seed`, 0, 0xffffffff), commands: [], durationTicks: int(demoValue.durationTicks, `${path}.demo.durationTicks`, 1, 1_000_000), loopPolicy: demoValue.loopPolicy }; if (demo.version !== version || demo.artifactDigest !== contract.simSha256 || demo.capabilityDigest !== capabilityLock.digest || demo.matchConfig.creationId !== trustedCreationId || demo.matchConfig.version !== version || demo.matchConfig.artifactDigest !== demo.artifactDigest || demo.matchConfig.capabilityDigest !== demo.capabilityDigest || demo.matchConfig.seed !== demo.seed) fail(`${path}.demo`, 'must pin the exact contract version, artifact, capability lock, and config seed'); if (!Array.isArray(demoValue.commands)) fail(`${path}.demo.commands`, 'must be an array'); let priorTick = -1; demo.commands = demoValue.commands.map((command, index) => { const item = object(command, `${path}.demo.commands[${index}]`); only(item, `${path}.demo.commands[${index}]`, ['tick', 'slot', 'command']); const tick = int(item.tick, `${path}.demo.commands[${index}].tick`, 0, demo.durationTicks); if (tick < priorTick) fail(`${path}.demo.commands[${index}].tick`, 'must be ordered'); priorTick = tick; return { tick, slot: int(item.slot, `${path}.demo.commands[${index}].slot`, 0, matchConfig.playerCount - 1), command: object(item.command, `${path}.demo.commands[${index}].command`) as JsonObject }; }); contract.demo = demo; }
  if (contract.demo !== undefined) {
    if (contract.demo.matchConfig.session?.purpose !== 'demo') fail(`${path}.demo.matchConfig.session`, 'must explicitly declare a non-persisting demo session');
    if (contract.demo.matchConfig.session.control === 'cpu-only' && contract.demo.commands.length !== 0) fail(`${path}.demo.commands`, 'cpu-only demos cannot also inject a command tape');
  }
  return contract;
}

/** Trusted readers must call this async verifier before using release data. */
export async function verifyCreationRuntimeContractV1(value: unknown, path = 'runtimeContract'): Promise<CreationRuntimeContractV1> {
  const contract = validateCreationRuntimeContractV1(value, path);
  await verifyGameplayCapabilityLock(contract.capabilityLock);
  return contract;
}
